import { ApiClient, type WhoAmIResponse } from '@carculate/api-client';

import { postAuthMessage } from './authChannel';
import { CONTACT_VERIFICATION_REQUIRED_EVENT } from './contactVerificationEvent';
import { SESSION_RESUMED_EVENT, createResumeCoordinator } from './sessionResume';

/** Window-event bij een 403 `fitter_only_not_allowed` (S021.1, F-16). */
export const FITTER_ONLY_BLOCKED_EVENT = 'carculate:fitter-only-blocked';

/**
 * Singleton ApiClient. baseUrl is leeg → relatieve `/api/...`-paths, die
 * door Vite (dev) en/of de reverse-proxy (prod) naar de backend gestuurd worden.
 * Same-origin = cookies werken zonder CORS-rituelen.
 */
export const apiClient = new ApiClient({
  baseUrl: '',
  onUnauthenticated: () => {
    // Navigatie wordt door <ProtectedRoute> opgepakt zodra een query/mutation
    // een NotAuthenticatedError gooit. Hier alleen een hook voor toekomstige logging.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('carculate:unauthenticated'));
    }
  },
  // Story fe-be-monteur-s1-basis (S021.1, F-16): een 403 `fitter_only_not_allowed`
  // betekent dat de sessie (inmiddels) alleen-monteur is. De FitterOnlyRouteGuard
  // luistert op dit event, ververst whoami en stuurt daarna naar /monteur.
  onFitterOnlyBlocked: () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(FITTER_ONLY_BLOCKED_EVENT));
    }
  },
  // Story be-fe-contactgegevens-bevestigen (AC 5/7): een 403 `contact_verification_required` van welke call
  // ook. De AuthProvider zet daarop het whoami-veld aan, en de poort toont het scherm (centraal, niet per call).
  onContactVerificationRequired: () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(CONTACT_VERIFICATION_REQUIRED_EVENT));
    }
  },
  // Story fe-be-monteur-s5c-ingelogd-blijven (S021.5c §3.2): op /monteur… eerst hervatten met
  // het toestel-token, vóór er een inlogscherm komt. De coördinator beslist of het mag.
  resumeSession: (info) => resumeCoordinator.attempt(info),
});

/**
 * S021.5c §3.2 — de ene hervat-coördinator van de app (gedeeld door `apiClient` en de eigen
 * client van de foto-wachtrij), zodat tien gelijktijdige 401's één resume-request geven.
 */
export const resumeCoordinator = createResumeCoordinator({
  resume: () => apiClient.flowResume(),
  whoAmIQuiet: () => apiClient.whoAmI({ quiet: true }),
  pathname: () => (typeof window !== 'undefined' ? window.location.pathname : ''),
  locks: typeof navigator !== 'undefined' && 'locks' in navigator ? (navigator.locks ?? null) : null,
  now: () => Date.now(),
  onResumed: (identity: WhoAmIResponse | null) => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_RESUMED_EVENT, { detail: identity }));
    }
    // S021.5d deel C: andere tabbladen verversen hun whoami (één call, nooit een eigen resume).
    postAuthMessage('login', identity?.userId ?? null);
  },
});
